import { useQuery } from '@tanstack/react-query'

import { QueryConfig, ExtractFnReturnType } from 'Lib/react-query'

import { BotzappingDataResponse } from './types'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { BotzappingDateParams, summaryParams } from './getProductData'
import { replaceWWWS } from 'Utils/stringHelper'
import { MONTH_AND_A_HALF, getCustomDate } from 'Utils/dates'
import { bzAxios } from 'Lib/bzAxios'
import { differenceInCalendarDays } from 'date-fns'

export type QueryFnType = typeof getBzBotMitigationData

export const getBzBotMitigationData = (
  from: number,
  endDate: number,
  domains?: Array<string>
): Promise<BotzappingDataResponse | Error> => {
  const { networkId, owner } = useUserStore.getState()
  const { fromDate: fromDateKey, toDate: toDateKey } = BotzappingDateParams
  const unixFrom = from * 1000
  const diffInDays: number = differenceInCalendarDays(unixFrom, endDate * 1000)
  const fromDate = new Date(unixFrom)
  const compareTrends = diffInDays < MONTH_AND_A_HALF
  const startDate: number = compareTrends
    ? fromDate.setDate(fromDate.getDate() + diffInDays) / 1000
    : from
  const params = {
    ...summaryParams,
    [fromDateKey]: startDate
      ? getCustomDate(startDate)
      : summaryParams[BotzappingDateParams.fromDate],
    [toDateKey]: endDate ? getCustomDate(endDate) : summaryParams[BotzappingDateParams.toDate],
    domains: owner ? null : domains ? replaceWWWS(domains) : null,
  }
  return bzAxios
    .get<BotzappingDataResponse>(`/summary?networkIdEnc=${networkId}&groupbydomain=true`, {
      params,
    })
    .then((res) => {
      return res.data
    })
    .catch((e) => {
      return e as Error
    })
}

export const useGetBotMitigationData = (
  params: { startDate: number; endDate: number; domains?: Array<string> },
  { config }: { config?: QueryConfig<QueryFnType> } = {}
) => {
  const accountId = useUserStore.getState().accountId
  const query = useQuery<ExtractFnReturnType<QueryFnType>>({
    queryKey: [
      'getBotMitigationData',
      [accountId, params?.startDate, params?.endDate, params?.domains],
    ],
    queryFn: () => getBzBotMitigationData(params.startDate, params.endDate, params?.domains),
    enabled: true,
    ...config,
  })

  const data = query.data && query.data instanceof Error ? undefined : query.data

  return { ...query, data }
}
